import React, { createContext, useContext, useEffect, useState } from 'react';
import { initLiffOnce, ensureLoggedIn, getIdTokenSafe } from '../lib/liffClient';
import { verifyLineAuth, getStoredToken, getStoredUserId, getStoredUserName, getStoredUserAvatar, clearAuthData } from '../services/api';
import { CFG } from '../config/env';

const AuthContext = createContext({
  status: 'idle',
  jwt: null,
  uid: null,
  name: null,
  avatar: null,
  error: null,
  retry: () => {},
  logout: () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: 'idle', // 'idle' | 'authenticating' | 'ready' | 'error'
    jwt: null,
    uid: null,
    name: null,
    avatar: null,
    error: null
  });

  const authenticate = async () => {
    try {
      setState(prev => ({ ...prev, status: 'authenticating', error: null }));

      // 1) Check if we already have valid tokens
      let jwt = getStoredToken();
      let uid = getStoredUserId();
      let name = getStoredUserName();
      let avatar = getStoredUserAvatar();

      // 2) If no JWT/uid, need to authenticate with LIFF
      if (!jwt || !uid) {
        console.log('No stored tokens found, attempting LIFF authentication');
        
        // Trigger LIFF login if not authenticated
        const redirected = await ensureLoggedIn(CFG.REDIRECT_URI);
        if (redirected) {
          console.log('Redirecting to LINE for authentication');
          return; // Will redirect out to LINE
        }

        // Get ID token from LIFF
        const idToken = await getIdTokenSafe();
        if (!idToken) {
          throw new Error('Failed to get ID token from LIFF');
        }

        console.log('Got ID token, verifying with backend');
        
        // Verify with backend and get JWT
        const authData = await verifyLineAuth(idToken);
        jwt = authData.access_token;
        uid = String(authData.user_id);
        name = authData.name || '';
        avatar = authData.avatar || '';
      }

      console.log('Authentication successful', { uid, name });
      
      setState({
        status: 'ready',
        jwt,
        uid,
        name,
        avatar,
        error: null
      });

    } catch (error) {
      console.error('Authentication error:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Authentication failed'
      }));
    }
  };

  const retry = () => {
    console.log('Retrying authentication');
    authenticate();
  };

  const logout = async () => {
    try {
      clearAuthData();
      setState({
        status: 'idle',
        jwt: null,
        uid: null,
        name: null,
        avatar: null,
        error: null
      });
      
      // Redirect to root for fresh login
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  useEffect(() => {
    authenticate();
  }, []);

  const value = {
    ...state,
    retry,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}